import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { REQUIRED_FIELD } from '../../utils/validation/input-validation';
import { DateInput, Select } from '../reusable';

const styles = {
  clockIcon: {
    fontSize: '18px',
  },
  scheduleBanner: {
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderLeft: '4px solid #FA5400',
    borderRadius: '4px',
    color: '#FA5400',
    display: 'flex',
    gap: '10px',
    padding: '12px 16px',
  },
  toggleContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
  },
  toggleInput: {
    height: 0,
    opacity: 0,
    width: 0,
  },
  toggleLabel: {
    display: 'inline-block',
    height: '20px',
    position: 'relative',
    width: '36px',
  },
};

const getToggleSlider = (isChecked) => ({
  backgroundColor: isChecked ? '#FA5400' : '#cccccc',
  border: 'none',
  borderRadius: '20px',
  bottom: 0,
  left: 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  top: 0,
  transition: '0.4s',
});

const getToggleKnob = (isChecked) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '50%',
  bottom: '3px',
  height: '14px',
  left: '3px',
  pointerEvents: 'none',
  position: 'absolute',
  transform: isChecked ? 'translateX(16px)' : 'translateX(0)',
  transition: '0.4s',
  width: '14px',
});

export const generateTimeOptions = (selectedDate) => {
  const times = [{ label: 'Select Time', value: '' }];
  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const isToday = selectedDate && moment(selectedDate).isSame(moment(), 'day');
  const currentHour = moment().hour();
  hours.forEach((hour) => {
    const label = `${hour}:00 AM`;
    const value24 = hour === 12 ? '00:00' : `${String(hour).padStart(2, '0')}:00`;
    const hour24 = hour === 12 ? 0 : hour;
    if (!isToday || hour24 > currentHour) {
      times.push({ label, value: value24 });
    }
  });
  hours.forEach((hour) => {
    const label = `${hour}:00 PM`;
    const value24 = hour === 12 ? '12:00' : `${hour + 12}:00`;
    const hour24 = hour === 12 ? 12 : hour + 12;
    if (!isToday || hour24 > currentHour) {
      times.push({ label, value: value24 });
    }
  });
  return times;
};

export const formatScheduledTime = (scheduledDate, scheduledTime) => {
  if (!scheduledDate || !scheduledTime) return null;
  const formattedDate = moment(scheduledDate).format('MMM D, YYYY');
  const timeParts = scheduledTime.split(':');
  const hour = parseInt(timeParts[0], 10);
  const minute = timeParts[1];
  let formattedTime;
  if (hour === 0) formattedTime = `12:${minute} AM`;
  else if (hour < 12) formattedTime = `${hour}:${minute} AM`;
  else if (hour === 12) formattedTime = `12:${minute} PM`;
  else formattedTime = `${hour - 12}:${minute} PM`;
  return { formattedDate, formattedTime };
};

export const ScheduleBanner = ({ scheduledDate, scheduledTime, variant }) => {
  const result = formatScheduledTime(scheduledDate, scheduledTime);
  if (!result) return null;
  const { formattedDate, formattedTime } = result;

  const message = variant === 'confirmation'
    ? (
      <span>
        Scheduled: <span style={{ fontWeight: 'bold' }}>{formattedDate}</span> at <span style={{ fontWeight: 'bold' }}>{formattedTime} PT</span>
      </span>
    )
    : (
      <span>
        This change will be applied automatically on <span style={{ fontWeight: 'bold' }}>{formattedDate}</span> at <span style={{ fontWeight: 'bold' }}>{formattedTime} PT</span>
      </span>
    );

  return (
    <div style={styles.scheduleBanner}>
      <span style={styles.clockIcon}>⏰</span>
      <span>{message}</span>
    </div>
  );
};

ScheduleBanner.defaultProps = {
  variant: 'default',
};

ScheduleBanner.propTypes = {
  scheduledDate: PropTypes.string.isRequired,
  scheduledTime: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['default', 'confirmation']),
};

const ScheduleForLater = ({
  disabled,
  disabledReason,
  id,
  scheduleForLater,
  scheduledDate,
  scheduledTime,
  onDateChange,
  onTimeChange,
  onToggle,
}) => (
  <>
    <article className="ncss-col-sm-12 va-sm-t mt1-sm" style={styles.toggleContainer}>
      <span>Schedule for later</span>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        id={id}
        style={{
          ...styles.toggleLabel,
          ...(disabled ? { cursor: 'not-allowed', opacity: 0.5 } : { cursor: 'pointer' }),
        }}
        onClick={() => { if (!disabled) onToggle(!scheduleForLater); }}
      >
        <span style={getToggleSlider(scheduleForLater)}>
          <span style={getToggleKnob(scheduleForLater)} />
        </span>
      </div>
    </article>
    {disabled && disabledReason && (
      <article className="ncss-col-sm-12 va-sm-t">
        <p className="body-2 text-color-secondary" style={{ fontSize: '12px', fontStyle: 'italic', marginTop: '4px' }}>
          {disabledReason}
        </p>
      </article>
    )}
    {scheduleForLater && (
      <>
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mt2-sm">
          <DateInput
            errorMessage={scheduleForLater && !scheduledDate ? REQUIRED_FIELD : ''}
            id={`${id}-date`}
            label="Date"
            min={moment().format('YYYY-MM-DD')}
            value={scheduledDate}
            onChange={onDateChange}
          />
        </article>
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mt2-sm">
          <Select
            errorMessage={scheduleForLater && !scheduledTime ? REQUIRED_FIELD : ''}
            id={`${id}-time`}
            label="Time"
            options={generateTimeOptions(scheduledDate)}
            value={scheduledTime}
            onChange={onTimeChange}
          />
        </article>
        {scheduledDate && scheduledTime && (
          <article className="ncss-col-sm-12 va-sm-t mt2-sm">
            <ScheduleBanner scheduledDate={scheduledDate} scheduledTime={scheduledTime} />
          </article>
        )}
      </>
    )}
  </>
);

ScheduleForLater.defaultProps = {
  disabled: false,
  disabledReason: null,
  id: 'scheduleForLaterToggle',
};

ScheduleForLater.propTypes = {
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  id: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  scheduledDate: PropTypes.string.isRequired,
  scheduledTime: PropTypes.string.isRequired,
  scheduleForLater: PropTypes.bool.isRequired,
};

export default ScheduleForLater;
